import { allJournalLines, SOURCE_AGENT } from './data';
import type { GateStatus, JournalEntryGroup, JournalLine } from './types';

export function formatCurrency(value: number, options: { compact?: boolean; accounting?: boolean } = {}): string {
  const abs = Math.abs(value);
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    notation: options.compact ? 'compact' : 'standard',
  });
  const formatted = formatter.format(abs);
  if (!options.accounting) {
    return value < 0 ? `-${formatted}` : formatted;
  }
  if (value < 0) {
    return `(${formatted})`;
  }
  return formatted;
}

export function formatCsvAmount(value: number | null): string {
  if (value === null) return '';
  return value.toFixed(2);
}

export function statusLabel(status: GateStatus): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'hold':
      return 'On hold';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Pending';
  }
}

export function statusForCsv(status: GateStatus | undefined): string {
  return (status ?? 'pending').toUpperCase();
}

export function escapeCsvCell(value: string | number | null): string {
  const rawText = String(value ?? '');
  const text = /^[=+\-@\t\r]/.test(rawText) ? `'${rawText}` : rawText;
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildNetsuiteCsv(
  statuses: Record<string, GateStatus>,
  entries?: JournalEntryGroup[],
  sourceAgent = SOURCE_AGENT,
): string {
  const header = [
    'External ID',
    'Journal Date',
    'Memo',
    'Account Number',
    'Account Name',
    'Debit',
    'Credit',
    'Department',
    'Class',
    'Subsidiary',
    'Approver',
    'HITL Status',
    'Source Agent',
  ];

  const lines: JournalLine[] = entries ? entries.flatMap(entry => entry.lines) : allJournalLines;

  const rows = lines.map(line => [
    line.externalId,
    line.journalDate,
    line.memo,
    line.accountNumber,
    line.accountName,
    formatCsvAmount(line.debit),
    formatCsvAmount(line.credit),
    line.department,
    line.className,
    line.subsidiary,
    line.approver,
    statusForCsv(statuses[line.gateId]),
    sourceAgent,
  ]);

  return [header, ...rows]
    .map(row => row.map(escapeCsvCell).join(','))
    .join('\n');
}

export function downloadText(filename: string, content: string, type: string): void {
  const blob = new Blob([content], { type });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
